/**
 * Risk Decision Archive (Roadmap H).
 * Routed at /risk-decisions — authenticated, tenant-scoped via JWT.
 * Surfaces material risk-score changes with the premium-signal +
 * connector-event context that likely drove them.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ArrowDownRight, Minus, Filter, ScrollText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const MAG_STYLES = {
  major: "border-rose-300 bg-rose-50 text-rose-800",
  moderate: "border-amber-300 bg-amber-50 text-amber-800",
  minor: "border-slate-200 bg-slate-50 text-slate-700",
};

function DirectionIcon({ direction, size = 14 }) {
  if (direction === "up") return <ArrowUpRight size={size} className="text-rose-600" />;
  if (direction === "down") return <ArrowDownRight size={size} className="text-emerald-600" />;
  return <Minus size={size} className="text-slate-400" />;
}

const HINT_LABEL = {
  premium_signal: "Premium signal",
  connector_event: "Connector",
};

export default function RiskDecisions() {
  const [days, setDays] = useState(30);
  const [minDelta, setMinDelta] = useState(0.5);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/risks/decisions?days=${days}&min_delta=${minDelta}&limit=200`);
      setItems(r.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, minDelta]);

  const stats = useMemo(() => {
    const total = items.length;
    const up = items.filter((i) => i.direction === "up").length;
    const down = items.filter((i) => i.direction === "down").length;
    const major = items.filter((i) => i.magnitude === "major").length;
    return { total, up, down, major };
  }, [items]);

  return (
    <main className="min-h-screen bg-[#f7f5fb] px-4 py-10" data-testid="risk-decisions-page">
      <div className="mx-auto max-w-6xl">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ek-purple">Decision-intelligence layer</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-ek-ink">Risk decision archive</h1>
            <p className="mt-1.5 max-w-2xl text-sm text-ek-ink/65">
              Every material residual-score change with the premium signals and
              connector deltas that fell in the same window. The audit trail
              that defends every number on your risk register.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-ek-ink/50" />
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-md border border-black/10 bg-white px-2 py-1 text-xs text-ek-ink" data-testid="risk-decisions-days">
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <select value={minDelta} onChange={(e) => setMinDelta(Number(e.target.value))} className="rounded-md border border-black/10 bg-white px-2 py-1 text-xs text-ek-ink" data-testid="risk-decisions-min-delta">
              <option value={0.5}>Δ ≥ 0.5</option>
              <option value={1}>Δ ≥ 1.0</option>
              <option value={2}>Δ ≥ 2.0</option>
              <option value={5}>Δ ≥ 5.0 (major only)</option>
            </select>
          </div>
        </header>

        {/* Stats */}
        <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Decisions tracked" value={stats.total} testid="rd-stat-total" />
          <Stat label="Score increases" value={stats.up} accent="text-rose-700" testid="rd-stat-up" />
          <Stat label="Score decreases" value={stats.down} accent="text-emerald-700" testid="rd-stat-down" />
          <Stat label="Major movements" value={stats.major} accent="text-amber-700" testid="rd-stat-major" />
        </section>

        {err && <p className="mb-4 text-sm text-rose-700" data-testid="risk-decisions-error">{err}</p>}
        {loading && items.length === 0 && <p className="text-sm text-ek-ink/55">Loading…</p>}
        {!loading && items.length === 0 && !err && (
          <p className="rounded-2xl border border-black/5 bg-white p-8 text-center text-sm text-ek-ink/55" data-testid="risk-decisions-empty">
            No material risk-score changes in this window. Try expanding the time range or lowering the delta threshold.
          </p>
        )}

        <section className="space-y-3" data-testid="risk-decisions-list">
          {items.map((it, idx) => (
            <article key={`${it.risk_id}-${it.date}-${idx}`} className="rounded-2xl border border-black/5 bg-white p-5" data-testid={`risk-decisions-item-${idx}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <DirectionIcon direction={it.direction} size={16} />
                    <span className="font-display text-base font-semibold text-ek-ink">{it.risk_title}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${MAG_STYLES[it.magnitude] || MAG_STYLES.minor}`}>{it.magnitude}</span>
                    {it.theme && <span className="rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-semibold text-ek-purple">{it.theme}</span>}
                  </div>
                  <p className="mt-1.5 text-xs text-ek-ink/65">
                    <span className="font-mono">{it.risk_id}</span>
                    {it.owner && <> · Owner: {it.owner}</>}
                    <> · {new Date(it.date).toLocaleDateString()}</>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-right">
                  <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Residual score</p>
                  <p className="mt-0.5 font-mono text-sm text-ek-ink" data-testid={`rd-score-${idx}`}>
                    {it.score_before} → <span className="font-semibold">{it.score_after}</span>
                    <span className={`ml-1.5 ${it.direction === "up" ? "text-rose-600" : it.direction === "down" ? "text-emerald-600" : "text-slate-400"}`}>
                      {it.delta > 0 ? "+" : ""}{it.delta}
                    </span>
                  </p>
                </div>
              </div>

              {it.context_hints && it.context_hints.length > 0 && (
                <div className="mt-4 border-t border-black/5 pt-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ek-ink/55">
                    <ScrollText size={11} /> Context near this change
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {it.context_hints.map((h, hi) => (
                      <li key={hi} className="flex items-start gap-2 text-xs">
                        <span className="mt-0.5 inline-block rounded-full bg-ek-purple/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-ek-purple">{HINT_LABEL[h.kind] || h.kind}</span>
                        <span className="flex-1 text-ek-ink/75">{h.label}</span>
                        {h.source && <span className="font-mono text-[10px] text-ek-ink/45">{h.source}</span>}
                        {h.ts && <span className="text-[10px] text-ek-ink/40">{new Date(h.ts).toLocaleDateString()}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}

function Stat({ label, value, accent = "text-ek-ink", testid }) {
  return (
    <div className="rounded-xl border border-black/5 bg-white px-4 py-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">{label}</p>
      <p className={`mt-0.5 font-display text-2xl font-semibold ${accent}`}>{value.toLocaleString()}</p>
    </div>
  );
}
